import { NavBarProvider } from '@cint/wonderment';
import { NotificationTray } from '@lucidhq/lucidium';
import { useEffect } from 'react';
import { lazy } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { addAccessTokenInterceptor } from '@/api/httpClient';
import { useAuth } from '@/auth';
import {
  FeatureFlagRoute,
  Navigation,
  NotFound,
  ProtectedRoute,
  SuspenseWrapper,
  UnauthorizedPage,
  ValidatedPanelRoute,
} from '@/components';
import { ROUTES } from '@/constants';
import { RESOURCES } from '@/utils/permissions';

import { FEATURE_FLAGS } from './utils/featureFlags';

const CreatePanelContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.CreatePanelContainer,
  }))
);
const CreateTranslationContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.CreateTranslationContainer,
  }))
);
const Panels = lazy(() =>
  import('@/pages').then((module) => ({ default: module.Panels }))
);
const PaymentMethods = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PaymentMethods,
  }))
);
const PaymentSettingsContainerV2 = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PaymentSettingsContainerV2,
  }))
);
const PanelPaymentSettingsContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PanelPaymentSettingsContainer,
  }))
);
const PaymentMethodsSidebar = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PaymentMethodsSidebar,
  }))
);
const SettingsContainer = lazy(() =>
  import('@/pages').then((module) => ({ default: module.SettingsContainer }))
);
const BrandingContainer = lazy(() =>
  import('@/pages').then((module) => ({ default: module.BrandingContainer }))
);
const PanelUrlContainer = lazy(() =>
  import('@/pages').then((module) => ({ default: module.PanelUrlContainer }))
);
const PanelPaymentMethodsV2 = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PanelPaymentMethodsV2,
  }))
);
const IncentiveSettingsContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.DynamicIncentiveModelSettingsContainer,
  }))
);
const ReferralProgramContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.ReferralProgramContainer,
  }))
);
const PanelistImportContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PanelistImportContainer,
  }))
);
const LeadImportContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.LeadImportContainer,
  }))
);
const ManualActivationContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.ManualActivationManagementContainer,
  }))
);
const PayoutContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PayoutContainer,
  }))
);
const CreateMarketingCampaignContainer = lazy(() =>
  import('@/pages/CreateMarketingCampaign').then((module) => ({
    default: module.CreateMarketingCampaignContainer,
  }))
);
const RecruitmentMarketingCallback = lazy(() =>
  import('@/pages/RecruitmentMarketingCallback').then((module) => ({
    default: module.RecruitmentMarketingCallback,
  }))
);
const NewCreateMarketingCampaignContainer = lazy(() =>
  import('@/pages/NewCreateMarketingCampaign').then((module) => ({
    default: module.CreateMarketingCampaignContainer,
  }))
);
const EditMarketingCampaignContainer = lazy(() =>
  import('@/pages/EditMarketingCampaign').then((module) => ({
    default: module.EditMarketingCampaignContainer,
  }))
);
const MarketingCampaigns = lazy(() =>
  import('@/pages/MarketingCampaigns/MarketingCampaigns').then((module) => ({
    default: module.MarketingCampaigns,
  }))
);
const VoucherCodes = lazy(() =>
  import('@/pages').then((module) => ({ default: module.VoucherCodes }))
);
const Support = lazy(() =>
  import('@/pages').then((module) => ({ default: module.Support }))
);
const BulkBanningContainer = lazy(() =>
  import('@/pages').then((module) => ({ default: module.BulkBanningContainer }))
);
const BulkBalanceAdjustContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.BulkBalanceAdjustContainer,
  }))
);
const SignalTemplateContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.SignalTemplatesContainer,
  }))
);
const SignalTemplates = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.SignalTemplatesList,
  }))
);
const Translations = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.Translations,
  }))
);
const TranslationsBulkUpdate = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.TranslationsBulkUpdateContainer,
  }))
);
const MarketingRecruitmentReport = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.MarketingRecruitmentReportContainer,
  }))
);
const PanelInsights = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.PanelInsights,
  }))
);
const BillingInvoicing = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.BillingInvoicingContainer,
  }))
);
const ExportPanelContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.ExportPanelContainer,
  }))
);
const EmailBlocklistContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.EmailBlocklistContainer,
  }))
);
const BuyerIDBlocklistContainer = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.BuyerIDBlocklistContainer,
  }))
);
const TargetedSampling = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.TargetedSampling,
  }))
);
const Profiling = lazy(() =>
  import('@/pages').then((module) => ({
    default: module.Profiling,
  }))
);

const CompanyPanelSettingsRedirect = () => {
  const { companyId, id } = useParams();
  return (
    <Navigate
      to={`${ROUTES.companies}/${companyId}${ROUTES.panels}/${id}/settings`}
      replace
    />
  );
};

const PanelSettingsRedirect = () => {
  const { id } = useParams();
  return (
    <Navigate
      to={`${ROUTES.companies}${ROUTES.panels}/${id}/settings`}
      replace
    />
  );
};

export const App = () => {
  const { getAccessToken } = useAuth();

  useEffect(() => {
    addAccessTokenInterceptor(getAccessToken);
  }, [getAccessToken]);

  return (
    <NavBarProvider>
      <Navigation />
      <main
        style={{
          width: '100%',
          maxWidth: '3840px',
          margin: '0 auto',
          minHeight: '100vh',
          background: '#fff',
          transition: 'margin-left 0.2s ease',
          boxSizing: 'border-box',
        }}
      >
        <NotificationTray
          lss={{ top: '68px', left: '50%', transform: 'translateX(-50%)' }}
        />
        <Routes>
          <Route
            path={ROUTES.home}
            element={<Navigate to={ROUTES.panels} replace />}
          />
          <Route
            path={ROUTES.metaCallback}
            element={
              <SuspenseWrapper>
                <RecruitmentMarketingCallback provider="meta" />
              </SuspenseWrapper>
            }
          />
          <Route
            path={ROUTES.googleCallback}
            element={
              <SuspenseWrapper>
                <RecruitmentMarketingCallback provider="google" />
              </SuspenseWrapper>
            }
          />
          <Route
            path={ROUTES.companies}
            element={<Navigate to={ROUTES.panels} replace />}
          />
          <Route
            path={`${ROUTES.createPanel}/:id`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.create}
              >
                <SuspenseWrapper>
                  <CreatePanelContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:id${ROUTES.panels}`}
            element={
              <ProtectedRoute
                action={RESOURCES.company.actions.view}
                resource={RESOURCES.company.id}
              >
                <SuspenseWrapper>
                  <Panels />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.panels}
            element={
              <ProtectedRoute
                action={RESOURCES.panel.actions.view}
                resource={RESOURCES.panel.id}
              >
                <SuspenseWrapper>
                  <Panels />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.paymentMethods}
            element={
              <ProtectedRoute
                resource={RESOURCES.pm.id}
                action={RESOURCES.pm.actions.view}
              >
                <SuspenseWrapper>
                  <PaymentMethods />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/panelRedemptionMethods/:panelPaymentMethodId/redemptionMethodsDetails/:paymentMethodId`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.pincentiv}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelPaymentSettingsContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/panelRedemptionMethods/:panelPaymentMethodId/redemptionMethodsDetails/:paymentMethodId`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelPaymentSettingsContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/panelRedemptionMethods/:panelPaymentMethodId/redemptionMethodsDetails/:paymentMethodId`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.pincentiv}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelPaymentSettingsContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.paymentMethods}/:id/*`}
            element={
              <ProtectedRoute
                resource={RESOURCES.pm.id}
                action={[
                  RESOURCES.pm.actions.view,
                  RESOURCES.pm.actions.viewcocodes,
                  RESOURCES.pm.actions.viewcodes,
                ]}
              >
                <SuspenseWrapper>
                  <PaymentMethodsSidebar />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.createPaymentMethod}
            element={
              <ProtectedRoute
                resource={RESOURCES.pm.id}
                action={RESOURCES.pm.actions.create}
              >
                <SuspenseWrapper>
                  <PaymentSettingsContainerV2 />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/settings`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <SettingsContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/branding`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <BrandingContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/url`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelUrlContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/panelRedemptionMethods/:panelPaymentMethodId/redemption`}
            element={
              <ProtectedRoute
                resource={RESOURCES.payout.id}
                action={RESOURCES.payout.actions.edit}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PayoutContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/panelRedemptionMethods`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelPaymentMethodsV2 />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/dynamicIncentiveSettings/:exchangeType?/:modelId?/:variantId?`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <FeatureFlagRoute
                    featureName={
                      FEATURE_FLAGS.IS_NEW_INCENTIVE_SETTINGS_ENABLED
                    }
                    renderWhenOn={
                      <SuspenseWrapper>
                        <IncentiveSettingsContainer />
                      </SuspenseWrapper>
                    }
                  />
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/panelistImport`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panelist.id}
                action={RESOURCES.panelist.actions.import}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelistImportContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/leadImport`}
            element={
              <ProtectedRoute
                resource={RESOURCES.lead.id}
                action={RESOURCES.lead.actions.batch}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <LeadImportContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/manualActivationManagement`}
            element={
              <ProtectedRoute
                resource={RESOURCES.activation.id}
                action={RESOURCES.activation.actions.direct}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <ManualActivationContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/createMarketingCampaign`}
            element={
              <ProtectedRoute
                resource={RESOURCES.marketing.id}
                action={RESOURCES.marketing.actions.editcamp}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <FeatureFlagRoute
                      featureName={
                        FEATURE_FLAGS.IS_NEW_MARKETING_CAMPAIGN_ENABLED
                      }
                      renderWhenOn={<NewCreateMarketingCampaignContainer />}
                      renderWhenOff={<CreateMarketingCampaignContainer />}
                    />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/marketingCampaigns/:campaignId`}
            element={
              <ProtectedRoute
                resource={RESOURCES.marketing.id}
                action={RESOURCES.marketing.actions.viewcamp}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <FeatureFlagRoute
                      featureName={
                        FEATURE_FLAGS.IS_NEW_MARKETING_CAMPAIGN_ENABLED
                      }
                      renderWhenOn={<EditMarketingCampaignContainer />}
                      renderWhenOff={<CreateMarketingCampaignContainer />}
                    />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/marketingCampaigns`}
            element={
              <ProtectedRoute
                resource={RESOURCES.marketing.id}
                action={RESOURCES.marketing.actions.viewcamp}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <MarketingCampaigns />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id/referralProgram`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <FeatureFlagRoute
                    featureName={FEATURE_FLAGS.IS_REFERRAL_PROGRAM_ENABLED}
                    renderWhenOn={
                      <SuspenseWrapper>
                        <ReferralProgramContainer />
                      </SuspenseWrapper>
                    }
                  />
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/settings`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <SettingsContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/branding`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <BrandingContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/url`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelUrlContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/panelRedemptionMethods/:panelPaymentMethodId/redemption`}
            element={
              <ProtectedRoute
                resource={RESOURCES.payout.id}
                action={RESOURCES.payout.actions.edit}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PayoutContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/panelRedemptionMethods`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelPaymentMethodsV2 />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/dynamicIncentiveSettings/:exchangeType/:modelId?/:variantId?`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <FeatureFlagRoute
                    featureName={
                      FEATURE_FLAGS.IS_NEW_INCENTIVE_SETTINGS_ENABLED
                    }
                    renderWhenOn={
                      <SuspenseWrapper>
                        <IncentiveSettingsContainer />
                      </SuspenseWrapper>
                    }
                  />
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/panelistImport`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panelist.id}
                action={RESOURCES.panelist.actions.import}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <PanelistImportContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/leadImport`}
            element={
              <ProtectedRoute
                resource={RESOURCES.lead.id}
                action={RESOURCES.lead.actions.batch}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <LeadImportContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/manualActivationManagement`}
            element={
              <ProtectedRoute
                resource={RESOURCES.activation.id}
                action={RESOURCES.activation.actions.direct}
              >
                <ValidatedPanelRoute>
                  <SuspenseWrapper>
                    <ManualActivationContainer />
                  </SuspenseWrapper>
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id/referralProgram`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.view}
              >
                <ValidatedPanelRoute>
                  <FeatureFlagRoute
                    featureName={FEATURE_FLAGS.IS_REFERRAL_PROGRAM_ENABLED}
                    renderWhenOn={
                      <SuspenseWrapper>
                        <ReferralProgramContainer />
                      </SuspenseWrapper>
                    }
                  />
                </ValidatedPanelRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path={`${ROUTES.companies}/:companyId${ROUTES.panels}/:id`}
            element={<CompanyPanelSettingsRedirect />}
          />
          <Route
            path={`${ROUTES.companies}${ROUTES.panels}/:id`}
            element={<PanelSettingsRedirect />}
          />

          <Route
            path={ROUTES.voucherCode}
            element={
              <ProtectedRoute
                resource={RESOURCES.pm.id}
                action={[
                  RESOURCES.pm.actions.viewcodes,
                  RESOURCES.pm.actions.viewcocodes,
                ]}
              >
                <SuspenseWrapper>
                  <VoucherCodes />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.panelists}
            element={
              <ProtectedRoute
                action={RESOURCES.panelist.actions.view}
                resource={RESOURCES.panelist.id}
              >
                <SuspenseWrapper>
                  <Support />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.panelists}/:id`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panelist.id}
                action={RESOURCES.panelist.actions.view}
              >
                <SuspenseWrapper>
                  <Support />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.bulkBanning}
            element={
              <ProtectedRoute
                resource={RESOURCES.bulkban.id}
                action={RESOURCES.bulkban.actions.create}
              >
                <SuspenseWrapper>
                  <BulkBanningContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.bulkBalanceAdjustment}
            element={
              <ProtectedRoute
                resource={RESOURCES.panelist.id}
                action={RESOURCES.panelist.actions.ledger}
              >
                <SuspenseWrapper>
                  <BulkBalanceAdjustContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.signalTemplates}
            element={
              <ProtectedRoute
                resource={RESOURCES.marketing.id}
                action={RESOURCES.marketing.actions.viewtmpl}
              >
                <SuspenseWrapper>
                  <SignalTemplates />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.createSignalTemplates}
            element={
              <ProtectedRoute
                resource={RESOURCES.marketing.id}
                action={RESOURCES.marketing.actions.edittmpl}
              >
                <SuspenseWrapper>
                  <SignalTemplateContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.signalTemplates}/:id`}
            element={
              <ProtectedRoute
                resource={RESOURCES.marketing.id}
                action={RESOURCES.marketing.actions.edittmpl}
              >
                <SuspenseWrapper>
                  <SignalTemplateContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.translations}
            element={
              <ProtectedRoute
                resource={RESOURCES.translat.id}
                action={RESOURCES.translat.actions.view}
              >
                <SuspenseWrapper>
                  <Translations />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.createTranslation}
            element={
              <ProtectedRoute
                action={RESOURCES.translat.actions.create}
                resource={RESOURCES.translat.id}
              >
                <SuspenseWrapper>
                  <CreateTranslationContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.translations}/:id`}
            element={
              <ProtectedRoute
                action={RESOURCES.translat.actions.update}
                resource={RESOURCES.translat.id}
              >
                <SuspenseWrapper>
                  <CreateTranslationContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.translationsBulkUpdate}`}
            element={
              <ProtectedRoute
                action={RESOURCES.translat.actions.update}
                resource={RESOURCES.translat.id}
              >
                <SuspenseWrapper>
                  <TranslationsBulkUpdate />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.recruitmentInsights}`}
            element={
              <ProtectedRoute
                action={RESOURCES.report.actions.view}
                resource={RESOURCES.report.id}
              >
                <SuspenseWrapper>
                  <MarketingRecruitmentReport />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.panelInsights}/:reportName`}
            element={
              <ProtectedRoute
                action={RESOURCES.report.actions.view}
                resource={RESOURCES.report.id}
              >
                <SuspenseWrapper>
                  <PanelInsights />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.billingInsights}`}
            element={
              <ProtectedRoute
                action={RESOURCES.report.actions.view}
                resource={RESOURCES.report.id}
              >
                <SuspenseWrapper>
                  <BillingInvoicing />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.exportPanel}/:id`}
            element={
              <ProtectedRoute
                resource={RESOURCES.panel.id}
                action={RESOURCES.panel.actions.export}
              >
                <SuspenseWrapper>
                  <ExportPanelContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.emailBlocklist}
            element={
              <ProtectedRoute
                resource={RESOURCES.blocklist.id}
                action={RESOURCES.blocklist.actions.edit}
              >
                <SuspenseWrapper>
                  <EmailBlocklistContainer />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.buyerIdBlocklist}
            element={
              <ProtectedRoute
                resource={RESOURCES.blocklist.id}
                action={RESOURCES.blocklist.actions.edit}
              >
                <FeatureFlagRoute
                  featureName={FEATURE_FLAGS.IS_BUYER_BLOCKING_ENABLED}
                  renderWhenOn={
                    <SuspenseWrapper>
                      <BuyerIDBlocklistContainer />
                    </SuspenseWrapper>
                  }
                />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.targetedSampling}
            element={
              <ProtectedRoute
                resource={RESOURCES.targetedSampling.id}
                action={RESOURCES.targetedSampling.actions.view}
              >
                <SuspenseWrapper>
                  <TargetedSampling />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.profiling}
            element={
              <ProtectedRoute
                action={RESOURCES.profiling.actions.view}
                resource={RESOURCES.profiling.id}
              >
                <SuspenseWrapper>
                  <Profiling />
                </SuspenseWrapper>
              </ProtectedRoute>
            }
          />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </NavBarProvider>
  );
};
